"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import {
  PricingTour,
  isPricingTourCompleted,
  restartPricingTour,
} from "@/components/tour/pricing-tour";
import { TourIndicator } from "@/components/tour/tour-indicator";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { FakeStripeCheckout } from "@/components/pricing/fake-stripe-checkout";
import { getPlans } from "@/app/actions/payment";
import { cn } from "@/lib/utils";

interface Plan {
  id: number;
  title: string;
  price: number;
  description: string;
  features: string[];
  numberOfListings: number;
  highlight?: boolean;
}

export default function PricingPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [shouldRunTour, setShouldRunTour] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    async function loadPlans() {
      const result = await getPlans();
      if (result.plans) {
        const plansWithHighlight = result.plans
          .filter((plan) => plan.title === "Ultimate Unlimited")
          .map((plan) => ({ ...plan, highlight: false }));
        setPlans(plansWithHighlight);
      }
      setLoading(false);
    }
    loadPlans();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-12 w-12 animate-spin text-[rgb(var(--primary-default))]" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-24 max-w-4xl">
      {/* Header Section (Matched to ContactPage Style) */}
      <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl p-8 mb-12 text-center shadow-lg">
        <h1 className="text-white mb-4 text-4xl font-bold tracking-tight">
          Simple, Transparent Pricing
        </h1>
        <p className="text-slate-200">
          The perfect plan for your real estate investing goals.
        </p>
      </div>

      {/* Pricing Card (Matched to Contact Form Card Style) */}
      <div className="flex justify-center">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className="border border-white/10 bg-slate-900/40 backdrop-blur-md p-6 shadow-lg rounded-xl w-full max-w-lg"
          >
            <CardHeader className="p-0 pb-6 text-center">
              <CardTitle className="text-white text-3xl">{plan.title}</CardTitle>
              <CardDescription className="text-slate-400 mt-2">
                {plan.description}
              </CardDescription>
              <div className="mt-6">
                <span className="text-white text-5xl font-bold">${plan.price}</span>
                <span className="text-slate-400 font-medium ml-2">/month</span>
              </div>
            </CardHeader>
            <CardContent className="p-0 mb-6">
              <div className="border border-white/5 rounded-lg bg-white/5 p-4 mb-6">
                <p className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-1">
                  Listings Included
                </p>
                <p className="text-2xl font-bold text-white">Unlimited</p>
              </div>
              <ul className="space-y-4">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3 text-slate-200">
                    <CheckCircle2 className="text-cyan-400 h-5 w-5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter className="p-0">
              <div className="w-full">
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <stripe-buy-button
                  buy-button-id="buy_btn_1SsY44GCOVrdqpcmLTV0Ww4u"
                  publishable-key="pk_test_51J2dFKGCOVrdqpcmoya4YCdnMris9DU3zApDFDbT2dhSfgFBgIHN2nyodgMPt4GKKHNlgYGtD2XZthYiiLkxTqFY00hAiRlCXS"
                />
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="border border-white/10 bg-slate-900 p-0 shadow-2xl">
          <VisuallyHidden>
            <DialogTitle>Checkout</DialogTitle>
          </VisuallyHidden>
          {selectedPlan && (
            <FakeStripeCheckout
              planId={selectedPlan.id}
              planTitle={selectedPlan.title}
              planPrice={selectedPlan.price}
              onCancel={() => { setShowCheckout(false); setSelectedPlan(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}